import { clearPersonAccess } from '@/lib/apiClient'
import * as React from 'react'
import {
  Outlet,
  NavLink,
  Link,
  useNavigate,
  useMatch,
  useLocation,
  useOutletContext,
  useParams,
} from 'react-router-dom'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Separator } from '@/components/ui/separator'

import { Toaster } from '@/components/ui/sonner'
import {
  ClipboardList,
  Home,
  Calendar,
  BookOpen,
  Users,
  Clipboard,
  Pencil,
  Award,
  BarChart4,
  Gauge,
  Activity,
  UsersRound,
  CircleUser,
  KeyRound,
  LogOut,
  ShieldCheck,
  ChartNoAxesCombined,
  ArrowLeftRight,
} from 'lucide-react'
import { useRunMode } from '@/context/run-mode'
import { useCurrentShow } from '@/context/current-show'
import { useRunSectionStatuses } from '@/hooks/useRunSectionStatuses'
import { useActorPermissions } from '@/hooks/useActorPermissions'
import { type RunSectionStatuses, type SectionIndicatorStatus } from '@/lib/run-section-status'
import { STATUS_LABELS } from '@/lib/show-status'
import { RunHud } from '@/components/run-hud'
import { Badge } from '@/components/ui/badge'
import { CalendarDays } from 'lucide-react'
import { showAware } from '@/lib/paths'
import { useShowSlug } from '@/hooks/useShowSlug'
import { formatDateRangeDateOnly } from '@/lib/date'
import { useShowStats, useLogout, useCurrentUser } from '@/api/hooks'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

type NavItem = {
  to: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  slugAware?: boolean
  adminOnly?: boolean
}

const setupNavItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: Home },
  { to: '/shows', label: 'Shows', icon: Calendar },
  { to: '/organizations', label: 'Organizations', icon: ClipboardList, adminOnly: true },
  { to: '/people', label: 'People', icon: Users, adminOnly: true },
  { to: '/clubs', label: 'Clubs', icon: UsersRound },
  { to: '/catalog/classes', label: 'Model Classes', icon: BookOpen },
  { to: '/show-series', label: 'Show Series', icon: ChartNoAxesCombined, slugAware: false },
]

const adminNavItems: NavItem[] = [
  { to: '/admin/users', label: 'User Administration', icon: ShieldCheck, slugAware: false },
]

const importExportNavItem: NavItem = {
  to: '/admin/show-transfer',
  label: 'Import & export',
  icon: ArrowLeftRight,
  slugAware: false,
}

const runNavItems: NavItem[] = [
  { to: '/run', label: 'Show Dashboard', icon: Gauge },
  { to: '/check-in', label: 'Check-In', icon: Clipboard },
  { to: '/judging', label: 'Judging', icon: Pencil },
  { to: '/awards', label: 'Awards', icon: Award },
  { to: '/reports', label: 'Reports', icon: BarChart4 },
  { to: '/entries', label: 'Entries', icon: ClipboardList },
  { to: '/entrants', label: 'Entrants', icon: Users },
  { to: '/show-series', label: 'Show Series', icon: ChartNoAxesCombined, slugAware: false },
  { to: '/status', label: 'Status', icon: Activity },
]

const runNavStatusKeys: Partial<Record<string, keyof RunSectionStatuses>> = {
  '/check-in': 'entries',
  '/judging': 'judging',
  '/awards': 'awards',
  '/reports': 'reports',
}

const STATUS_META: Record<SectionIndicatorStatus, { label: string; className: string }> = {
  not_started: {
    label: 'Not started',
    className: 'bg-muted-foreground/50',
  },
  in_progress: {
    label: 'In progress',
    className: 'bg-amber-500',
  },
  complete: {
    label: 'Complete',
    className: 'bg-emerald-500',
  },
  loading: {
    label: 'Loading status…',
    className: 'bg-muted-foreground/70 animate-pulse',
  },
  unknown: {
    label: 'Status unavailable',
    className: 'bg-muted-foreground/60',
  },
}

function StatusIndicator({
  status,
  sectionLabel,
}: {
  status: SectionIndicatorStatus
  sectionLabel: string
}) {
  const meta = STATUS_META[status]
  const accessibleLabel = `${sectionLabel} status: ${meta.label}`

  return (
    <span
      role="status"
      aria-label={accessibleLabel}
      title={meta.label}
      data-status={status}
      className={`ml-2 inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${meta.className}`}
    />
  )
}

function CloseMobileSidebarOnNavigation() {
  const location = useLocation()
  const { setOpenMobile } = useSidebar()
  React.useEffect(() => {
    setOpenMobile(false)
  }, [location.key, setOpenMobile])
  return null
}

export function AppShell() {
  const { seriesId } = useParams()
  const seriesChooserRoute = useMatch('/show-series')
  const setupRoute = useMatch('/setup')
  const showSetupRoute = useMatch('/:slug/setup')
  const isSetupPage = Boolean(setupRoute || showSetupRoute)
  const { mode } = useRunMode()
  const routedShow = useOutletContext()
  const { currentShow, clearCurrentShow } = useCurrentShow()
  const { pathname } = useLocation()
  const isRunMode = mode === 'run' && Boolean(currentShow)
  const runStatusesState = useRunSectionStatuses(currentShow, { enabled: isRunMode })
  const logoutMutation = useLogout()
  const currentUserQuery = useCurrentUser()
  const currentUser = currentUserQuery.data ?? null
  const isAdmin = !currentUserQuery.isLoading && currentUser?.isAdmin === true
  const navItems = React.useMemo(() => {
    let base: NavItem[]
    if (isRunMode) {
      if (currentShow?.labelOnlyMode) {
        base = runNavItems.filter(item => item.to === '/run' || item.to === '/show-series')
      } else {
        base = runNavItems
      }
    } else {
      base = setupNavItems.filter(item => !item.adminOnly || isAdmin)
    }

    if (isAdmin) {
      return [
        ...base,
        { divider: true },
        ...adminNavItems,
        ...(!isRunMode ? [importExportNavItem] : []),
      ]
    }
    return base
  }, [isRunMode, currentShow?.labelOnlyMode, isAdmin])
  // The Entries and Entrants badges come from show stats, which stewards cannot read.
  const canReadStats = useActorPermissions(currentShow?.id).can('reports:results')
  const showStatsQuery = useShowStats(isRunMode && canReadStats ? currentShow?.id : undefined)
  const navigate = useNavigate()
  const currentSlug = useShowSlug()
  const homeLinkTarget = isRunMode ? showAware('/run', { currentSlug }) : '/'

  const handleExitRunMode = React.useCallback(() => {
    navigate('/')
  }, [navigate])

  const handleLogout = React.useCallback(async () => {
    if (logoutMutation.isPending) return

    try {
      await logoutMutation.mutateAsync()
    } catch {
      // Treat network failures the same as a successful logout attempt.
    } finally {
      clearPersonAccess()
      clearCurrentShow()
      try {
        sessionStorage.removeItem('__testCurrentShow__')
        sessionStorage.removeItem('__testUiMode__')
      } catch {
        // Ignore storage access issues (e.g., unavailable in test environments)
      }
      try {
        localStorage.removeItem('honeyshow/uiMode')
      } catch {
        // Ignore storage access issues
      }
      navigate('/login', { replace: true })
    }
  }, [clearCurrentShow, logoutMutation, navigate])

  const handleChangePassword = React.useCallback(() => {
    navigate('/account/change-password')
  }, [navigate])

  return (
    <SidebarProvider>
      <CloseMobileSidebarOnNavigation />
      <div
        className={isSetupPage ? 'flex h-svh w-full overflow-hidden' : 'flex min-h-screen w-full'}
      >
        <Sidebar>
          <SidebarHeader className="py-4">
            <Link
              to={homeLinkTarget}
              className="flex items-center px-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm"
            >
              <ClipboardList className="mr-2 h-6 w-6" />
              <span className="text-xl font-semibold">Honey Show</span>
            </Link>
            <div className="flex items-center px-3 mt-1">
              <span className="text-sm text-muted-foreground">Entry Management System</span>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <SidebarMenu>
              {navItems.map((item, index) => {
                if ('divider' in item) {
                  return (
                    <SidebarMenuItem
                      key={`divider-${index}`}
                      aria-hidden="true"
                      className="px-3 pt-2 pointer-events-none"
                    >
                      <Separator className="bg-border/60" />
                    </SidebarMenuItem>
                  )
                }

                const statusKey = isRunMode ? runNavStatusKeys[item.to] : undefined
                const isSeriesItem = item.to === '/show-series'
                const seriesActive = isSeriesItem && Boolean(seriesId || seriesChooserRoute)
                const destination = isSeriesItem
                  ? isRunMode && currentShow?.id
                    ? `/show-series/for-show/${currentShow.id}`
                    : '/show-series'
                  : isRunMode && item.slugAware !== false
                    ? showAware(item.to, { currentSlug })
                    : item.to
                if (isSeriesItem || item === importExportNavItem) {
                  const active = isSeriesItem
                    ? seriesActive
                    : pathname === '/admin/show-import' || pathname === '/admin/show-transfer'
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild isActive={active} tooltip={item.label}>
                        <Link to={destination} aria-current={active ? 'page' : undefined}>
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1 text-left">{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  )
                }
                let indicatorStatus: SectionIndicatorStatus | undefined

                if (statusKey) {
                  if (runStatusesState.statuses) {
                    indicatorStatus = runStatusesState.statuses[statusKey]
                  } else if (runStatusesState.isLoading) {
                    indicatorStatus = 'loading'
                  } else if (runStatusesState.isError) {
                    indicatorStatus = 'unknown'
                  }
                }

                const showDivider = isRunMode && item.to === '/entries'
                const badgeValue =
                  isRunMode && showStatsQuery.data
                    ? item.to === '/entries'
                      ? showStatsQuery.data.entriesCount
                      : item.to === '/entrants'
                        ? showStatsQuery.data.entrantsCount
                        : undefined
                    : undefined
                const displayBadge =
                  typeof badgeValue === 'number' && Number.isFinite(badgeValue) && badgeValue >= 0

                return (
                  <React.Fragment key={item.to}>
                    {showDivider ? (
                      <SidebarMenuItem aria-hidden="true" className="px-3 pt-2 pointer-events-none">
                        <Separator className="bg-border/60" />
                      </SidebarMenuItem>
                    ) : null}
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={
                          pathname === destination ||
                          (destination !== '/' && pathname.startsWith(`${destination}/`))
                        }
                        tooltip={item.label}
                      >
                        <NavLink to={destination}>
                          <item.icon className="h-4 w-4" />
                          <span className="flex-1 text-left">{item.label}</span>
                          {indicatorStatus ? (
                            <StatusIndicator status={indicatorStatus} sectionLabel={item.label} />
                          ) : null}
                          {displayBadge ? (
                            <SidebarMenuBadge className="bg-sidebar-accent/20 text-sidebar-accent-foreground">
                              {badgeValue.toLocaleString()}
                            </SidebarMenuBadge>
                          ) : null}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  </React.Fragment>
                )
              })}
            </SidebarMenu>
          </SidebarContent>
          <SidebarFooter>
            <a
              href="/connect/server"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm underline"
            >
              Show server <span className="sr-only">(opens in a new tab)</span>
            </a>
            <div className="flex items-center justify-between p-4">
              <div className="text-xs text-muted-foreground">
                <a
                  href="https://github.com/erinoerin/honey-show-app/blob/main/docs/USER_GUIDE.md"
                  target="_blank"
                  rel="noreferrer noopener"
                  className="hover:underline"
                >
                  Guide
                </a>
              </div>
              <div className="flex items-center gap-3">
                {isRunMode ? (
                  <button
                    type="button"
                    onClick={handleExitRunMode}
                    className="text-xs font-medium text-muted-foreground hover:text-primary underline disabled:opacity-50 disabled:hover:text-muted-foreground"
                  >
                    Exit Show Mode
                  </button>
                ) : null}
              </div>
            </div>
          </SidebarFooter>
        </Sidebar>

        <div className="min-w-0 flex-1 flex flex-col overflow-hidden w-full">
          <header className="min-h-16 shrink-0 border-b border-gray-200 flex flex-wrap items-center gap-3 px-4 py-3 sm:px-6 bg-background">
            <div className="flex min-w-0 flex-1 basis-72 items-center gap-3">
              <SidebarTrigger className="mr-1 shrink-0" />
              {isRunMode && currentShow ? (
                <div
                  className="flex min-w-0 flex-1 flex-col gap-1 leading-tight text-sm"
                  data-testid="run-header-summary"
                >
                  <h1
                    className="m-0 whitespace-normal break-words text-primary text-sm font-semibold md:text-base"
                    title={currentShow.name}
                  >
                    {currentShow.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <Badge variant="secondary" className="shrink-0">
                      {STATUS_LABELS[currentShow.status] ?? 'Draft'}
                    </Badge>
                    <span className="flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
                      <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                      {formatDateRangeDateOnly(currentShow.startDate, currentShow.endDate)}
                    </span>
                  </div>
                </div>
              ) : (
                <div
                  className="flex min-w-0 flex-col leading-tight text-sm"
                  data-testid="setup-header"
                >
                  <span className="text-sm font-semibold md:text-base">Honey Show workspace</span>
                  {!isSetupPage && (
                    <span className="text-xs text-muted-foreground hidden sm:inline">
                      Visit the Shows page to choose a show for setup or run mode.
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="ml-auto flex max-w-full items-center gap-3">
              {isRunMode && <RunHud />}
              {currentUser ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0 rounded-full border-border/70 bg-background text-sm font-semibold"
                      aria-label="Account menu"
                      data-testid="account-menu-trigger"
                    >
                      <CircleUser className="h-5 w-5" aria-hidden="true" />
                      <span className="sr-only">Open account menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="flex flex-col gap-1">
                      <span className="text-xs uppercase tracking-wide text-muted-foreground">
                        Signed in as
                      </span>
                      <span className="text-sm font-medium text-foreground break-all">
                        {currentUser.email}
                      </span>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => navigate('/account')}
                      data-testid="account-menu-my-account"
                    >
                      <div className="flex items-center gap-2">
                        <CircleUser className="h-4 w-4" aria-hidden="true" />
                        <span>My Account</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onSelect={handleChangePassword}
                      data-testid="account-menu-change-password"
                    >
                      <div className="flex items-center gap-2">
                        <KeyRound className="h-4 w-4" aria-hidden="true" />
                        <span>Change Password</span>
                      </div>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={handleLogout}
                      disabled={logoutMutation.isPending}
                      data-testid="account-menu-logout"
                    >
                      <div className="flex items-center gap-2">
                        <LogOut className="h-4 w-4" aria-hidden="true" />
                        <span>{logoutMutation.isPending ? 'Logging out…' : 'Log out'}</span>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-auto p-6 w-full max-w-6xl mx-auto">
            <Outlet context={routedShow} />
          </main>

          <footer className="border-t border-gray-200 p-4 text-center text-sm text-muted-foreground">
            © {new Date().getFullYear()} Honey Show Entry Management System. All rights reserved.
          </footer>
        </div>

        <Toaster />
      </div>
    </SidebarProvider>
  )
}
